import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Building2, BookOpen, ArrowRightLeft, FileText, X } from 'lucide-react';
import type { SearchResult } from '@/types';
import { searchApi } from '@/api/client';

// --- Constants ---------------------------------------------------------------

const RECENT_SEARCHES_KEY = 'consolidateai_recent_searches';
const MAX_RECENT = 5;

const typeIcons: Record<string, React.ElementType> = {
  entity: Building2,
  account: BookOpen,
  transaction: ArrowRightLeft,
  journal: FileText,
};

const typeRoutes: Record<string, string> = {
  entity: '/entities',
  account: '/chart-of-accounts',
  transaction: '/trial-balances',
  journal: '/consolidation',
};

function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(query: string) {
  try {
    const recent = getRecentSearches().filter((s) => s !== query);
    recent.unshift(query);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {
    // silently fail
  }
}

// --- Component ---------------------------------------------------------------

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load recent searches on open
  useEffect(() => {
    if (isOpen) {
      setRecentSearches(getRecentSearches());
    }
  }, [isOpen]);

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
        setResults([]);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await searchApi.search(searchQuery);
      setResults(Array.isArray(res) ? res : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // Navigate to result
  const handleSelect = (result: SearchResult) => {
    saveRecentSearch(query);
    setIsOpen(false);
    setQuery('');
    setResults([]);
    const route = typeRoutes[result.type] || '/';
    navigate(route);
  };

  // Use recent search
  const handleRecentClick = (search: string) => {
    setQuery(search);
    performSearch(search);
  };

  // Group results by type
  const groupedResults: Record<string, SearchResult[]> = {};
  results.forEach((r) => {
    if (!groupedResults[r.type]) groupedResults[r.type] = [];
    groupedResults[r.type].push(r);
  });

  return (
    <div className="relative" ref={containerRef}>
      {/* Search trigger */}
      <button
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-100 transition-colors w-56"
      >
        <Search className="w-3.5 h-3.5" />
        <span className="flex-1 text-left">Search...</span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-white border border-gray-200 rounded">
          <span className="text-xs">&#8984;</span>K
        </kbd>
      </button>

      {/* Search dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
          {/* Search input */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              className="flex-1 text-sm bg-transparent border-none outline-none placeholder-gray-400"
              placeholder="Search entities, accounts, transactions..."
            />
            {loading && <Loader2 className="w-4 h-4 text-gray-400 animate-spin flex-shrink-0" />}
            {query && !loading && (
              <button
                onClick={() => {
                  setQuery('');
                  setResults([]);
                  inputRef.current?.focus();
                }}
                className="p-0.5 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Results */}
          <div className="max-h-80 overflow-y-auto">
            {/* No query - show recent searches */}
            {!query && recentSearches.length > 0 && (
              <div className="p-3">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">
                  Recent Searches
                </p>
                {recentSearches.map((search, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleRecentClick(search)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  >
                    <Search className="w-3 h-3 text-gray-300" />
                    {search}
                  </button>
                ))}
              </div>
            )}

            {/* Query but no results */}
            {query && !loading && results.length === 0 && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-gray-400">No results found for "{query}"</p>
              </div>
            )}

            {/* Grouped results */}
            {Object.entries(groupedResults).map(([type, items]) => {
              const Icon = typeIcons[type] || FileText;
              return (
                <div key={type}>
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                      {type}s
                    </p>
                  </div>
                  {items.slice(0, 5).map((result) => (
                    <button
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleSelect(result)}
                      className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center mt-0.5">
                        <Icon className="w-3.5 h-3.5 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{result.title}</p>
                        <p className="text-xs text-gray-500 truncate">{result.subtitle}</p>
                        {result.highlight && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{result.highlight}</p>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-300 font-mono mt-1">
                        {(result.relevance_score * 100).toFixed(0)}%
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {(results.length > 0 || query) && (
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
              <span className="text-[10px] text-gray-400">
                {results.length} result{results.length !== 1 ? 's' : ''}
              </span>
              <span className="text-[10px] text-gray-400">
                Press <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono">Esc</kbd> to close
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
